import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { SBLogo } from "../components/WebLayout";
import Seo from "../components/Seo";
import { Lock, AlertCircle, Loader2, CheckCircle2 } from "lucide-react";

export default function AuthResetPassword() {
  const nav = useNavigate();
  const [state, setState] = useState("checking"); // checking | ready | invalid | saving | done
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    // Flag recovery active so any late-firing auth guards don't redirect us away
    try { sessionStorage.setItem("spotbounty_password_recovery_active", "true"); } catch { /* ignore */ }
    (async () => {
      const { data } = await supabase.auth.getSession();
      if (!data?.session) { setState("invalid"); return; }
      setState("ready");
    })();
    return () => {
      // Clear flag on unmount
      try { sessionStorage.removeItem("spotbounty_password_recovery_active"); } catch { /* ignore */ }
    };
  }, []);

  async function submit(e) {
    e.preventDefault(); setErr("");
    if (pw.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (pw !== pw2) { setErr("Passwords do not match."); return; }
    setState("saving");
    const { error } = await supabase.auth.updateUser({ password: pw });
    if (error) {
      setState("ready");
      setErr("We couldn't update your password. The reset link may have expired.");
      return;
    }
    try { await supabase.auth.signOut(); } catch { /* ignore */ }
    try { sessionStorage.removeItem("spotbounty_password_recovery_active"); } catch { /* ignore */ }
    setState("done");
  }

  return (
    <div className="min-h-screen bg-[#080B0D] text-white flex flex-col">
      <Seo title="Reset Password" description="Reset your SpotBounty password" path="/auth/reset-password" noindex />
      <header className="border-b border-[#2A2F34]"><div className="max-w-3xl mx-auto px-6 py-4"><Link to="/"><SBLogo /></Link></div></header>
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="sb-card p-8 max-w-md w-full" data-testid="auth-reset-page">
          <div className="sb-eyebrow">Reset password</div>
          {state === "checking" && (
            <div className="mt-4 flex items-center gap-2 text-[#A1A1AA] text-[13px]"><Loader2 size={14} className="animate-spin" /> Verifying reset link…</div>
          )}
          {state === "invalid" && (
            <div data-testid="auth-reset-invalid">
              <h1 className="mt-2 text-[24px] font-black">Link expired or invalid</h1>
              <p className="mt-3 text-[13px] text-[#A1A1AA]">This reset link has expired or is invalid. Please request a new password reset from your login page.</p>
              <div className="mt-4 space-y-2 text-[13px]">
                <Link to="/admin/login" className="block text-[#FFC107] hover:underline">Owner Admin → /admin/login</Link>
                <Link to="/scouts/login" className="block text-[#FFC107] hover:underline">Scout → /scouts/login</Link>
                <Link to="/operator/login" className="block text-[#FFC107] hover:underline">Operator → /operator/login</Link>
              </div>
            </div>
          )}
          {(state === "ready" || state === "saving") && (
            <form onSubmit={submit} className="mt-4 space-y-3" data-testid="auth-reset-form">
              <h1 className="text-[24px] font-black">Set a new password</h1>
              <label className="block"><div className="sb-label">New password</div>
                <div className="relative"><Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
                <input type="password" required minLength={8} className="sb-input pl-9" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="auth-reset-pw" /></div></label>
              <label className="block"><div className="sb-label">Confirm new password</div>
                <div className="relative"><Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
                <input type="password" required minLength={8} className="sb-input pl-9" value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="auth-reset-pw2" /></div></label>
              {err && <div className="text-[12px] text-[#EF4444] flex items-center gap-1"><AlertCircle size={12} />{err}</div>}
              <button type="submit" disabled={state === "saving"} className="sb-btn-primary w-full" data-testid="auth-reset-submit">
                {state === "saving" ? <Loader2 size={14} className="animate-spin" /> : "Update password"}
              </button>
            </form>
          )}
          {state === "done" && (
            <div data-testid="auth-reset-done">
              <div className="mt-2 flex items-center gap-2 text-[#22C55E]"><CheckCircle2 size={16} /><h1 className="text-[22px] font-black">Password updated</h1></div>
              <p className="mt-3 text-[13px] text-[#A1A1AA]">You can now sign in with your new password.</p>
              <div className="mt-4 space-y-2 text-[13px]">
                <Link to="/admin/login" className="block text-[#FFC107] hover:underline">Sign in as Owner Admin</Link>
                <Link to="/scouts/login" className="block text-[#FFC107] hover:underline">Sign in as Scout</Link>
                <Link to="/operator/login" className="block text-[#FFC107] hover:underline">Sign in as Operator</Link>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
